import { keepPreviousData, useQuery } from "@tanstack/react-query";
import URI from "urijs";
import concat from "lodash/concat";
import { EventsNotifications } from "@graylog/server-api";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import fetch from "logic/rest/FetchProvider";
import CancellablePromise from "logic/rest/CancellablePromise";
import { defaultOnError } from "util/conditional/onError";
export const EVENT_NOTIFICATIONS_QUERY_KEY = ["event-notifications"];
const sourceUrl = "/events/notifications";
const eventNotificationsUrl = ({
  segments = [],
  query = {}
}) => {
  const uri = new URI(sourceUrl);
  const nextSegments = concat(uri.segment(), segments);
  uri.segmentCoded(nextSegments);
  uri.query(query);
  return qualifyUrl(uri.resource());
};
export const fetchEventNotifications = (searchParams) => EventsNotifications.getPage(
  searchParams.sort.attributeId,
  searchParams.page,
  searchParams.pageSize,
  searchParams.query || void 0,
  searchParams.sort.direction
).then(({ elements, query, attributes, pagination }) => ({
  list: elements,
  pagination: {
    count: pagination.count,
    total: pagination.total,
    page: pagination.page,
    perPage: pagination.per_page,
    query
  },
  attributes
}));
export const fetchAllEventNotifications = () => fetch("GET", eventNotificationsUrl({ query: { per_page: 0 } }));
export const fetchLegacyEventNotificationTypes = () => fetch("GET", eventNotificationsUrl({ segments: ["legacy", "types"] }));
export const getEventNotification = (notificationId) => EventsNotifications.get(encodeURIComponent(notificationId)).then(
  (response) => response,
  (error) => {
    if (error.status === 404) {
      UserNotification.error(
        `Unable to find Event Notification with id <${notificationId}>, please ensure it wasn't deleted.`,
        "Could not retrieve Event Notification"
      );
    }
    throw error;
  }
);
export const createEventNotification = (notification) => {
  const { share_request, ...rest } = notification;
  return fetch("POST", eventNotificationsUrl({}), { entity: rest, share_request }).then(
    (response) => {
      UserNotification.success(
        "Notification created successfully",
        `Notification "${notification.title}" was created successfully.`
      );
      return response;
    },
    (error) => {
      if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
        UserNotification.error(
          `Creating Notification "${notification.title}" failed with status: ${error}`,
          "Could not save Notification"
        );
      }
      throw error;
    }
  );
};
export const updateEventNotification = (notificationId, notification) => EventsNotifications.update(encodeURIComponent(notificationId), notification).then(
  (response) => {
    UserNotification.success(
      "Notification updated successfully",
      `Notification "${notification.title}" was updated successfully.`
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        `Updating Notification "${notification.title}" failed with status: ${error}`,
        "Could not update Notification"
      );
    }
    throw error;
  }
);
export const deleteEventNotification = (notification) => EventsNotifications.remove(encodeURIComponent(notification.id)).then(
  (response) => {
    UserNotification.success(
      "Notification deleted successfully",
      `Notification "${notification.title}" was deleted successfully.`
    );
    return response;
  },
  (error) => {
    UserNotification.error(
      `Deleting Notification "${notification.title}" failed with status: ${error}`,
      "Could not delete Notification"
    );
    throw error;
  }
);
export const testEventNotification = (notification) => CancellablePromise.of(EventsNotifications.test(notification));
export const testPersistedEventNotification = (notification) => EventsNotifications.testBynotificationId(encodeURIComponent(notification.id));
export const keyFn = (searchParams) => [
  ...EVENT_NOTIFICATIONS_QUERY_KEY,
  "overview-paginated",
  ...searchParams ? [searchParams] : []
];
export const useEventNotificationsPaginated = (searchParams, { enabled } = { enabled: true }) => {
  const { data, refetch, isInitialLoading } = useQuery({
    queryKey: keyFn(searchParams),
    queryFn: () => defaultOnError(
      fetchEventNotifications(searchParams),
      "Loading event notifications failed with status",
      "Could not load event notifications"
    ),
    placeholderData: keepPreviousData,
    retry: false,
    enabled
  });
  return {
    data,
    refetch,
    isInitialLoading
  };
};
export const useEventNotifications = () => useQuery({
  queryKey: [...EVENT_NOTIFICATIONS_QUERY_KEY, "all"],
  queryFn: fetchAllEventNotifications
});
export const useLegacyEventNotificationTypes = () => useQuery({
  // Deliberately not under EVENT_NOTIFICATIONS_QUERY_KEY: these are static plugin type
  // descriptors, not event notification entity data, so they should not be refetched on
  // event notification mutations.
  queryKey: ["event-notification-legacy-types"],
  queryFn: fetchLegacyEventNotificationTypes
});
export const useEventNotification = (notificationId) => useQuery({
  queryKey: [...EVENT_NOTIFICATIONS_QUERY_KEY, "id", notificationId],
  queryFn: () => defaultOnError(
    getEventNotification(notificationId),
    "Loading event notification failed with status",
    "Could not load event notification"
  ),
  retry: false
});
export default useEventNotificationsPaginated;
